import { cn, Separator, Text } from '@risksmart-app/atomic-ui';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types/risk-assessment-result-config';
import { type ReactNode, useEffect, useRef } from 'react';

import {
  MatrixPreview,
  RatingsPreview,
} from '../blocks/risk-scoring-config-preview';

export interface LivePreviewLang {
  multiImpactHeader: string;
  impactLikelihoodHeader: string;
  likelihoodLabel: string;
  impactLabel: string;
  likelihoodAxisLabel: string;
  impactAxisLabel: string;
}

interface LivePreviewPanelProps {
  config: RiskAssessmentResultConfig;
  lang: LivePreviewLang;
  /**
   * Reports the preview's intrinsic content width so the caller can size the
   * drawer. The measured element is `w-max` (content-sized, not drawer-sized),
   * so it can't feed back into a resize loop.
   */
  onContentWidthChange?: (width: number) => void;
}

const PreviewSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className={cn('flex flex-col gap-1')}>
    <Text preset={'body-md'} className={cn('font-semibold text-primary')}>
      {title}
    </Text>
    <div
      className={cn(
        'rounded-lg border border-neutral-border bg-neutral p-4 w-max min-w-full'
      )}
    >
      {children}
    </div>
  </div>
);

/**
 * Read-only render of the in-progress scoring config for the page tools drawer,
 * reflecting the live editing store. The matrix can be wider than the drawer, so
 * the outer container scrolls.
 */
export const LivePreviewPanel = ({
  config,
  lang,
  onContentWidthChange,
}: LivePreviewPanelProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !onContentWidthChange) {
      return;
    }
    const observer = new ResizeObserver(() => {
      onContentWidthChange(el.offsetWidth);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [onContentWidthChange]);

  return (
    <div className={cn('overflow-auto')}>
      <div ref={contentRef} className={cn('flex w-max flex-col gap-4')}>
        <PreviewSection title={lang.multiImpactHeader}>
          <RatingsPreview
            config={config}
            likelihoodLabel={lang.likelihoodLabel}
            impactLabel={lang.impactLabel}
          />
        </PreviewSection>
        <Separator />
        <PreviewSection title={lang.impactLikelihoodHeader}>
          <MatrixPreview
            config={config}
            likelihoodAxisLabel={lang.likelihoodAxisLabel}
            impactAxisLabel={lang.impactAxisLabel}
          />
        </PreviewSection>
      </div>
    </div>
  );
};
